import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import TablePagination from "@mui/material/TablePagination";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import { useConfirm } from "../../../common/confirmDialog/useConfirm";
import { BlogImage } from "./BlogImage";
import { withCdn } from "../blogTheme";
import { fetchMedia, deleteMedia } from "../mediaSlice";

const ROW_SIZE = 10;

export function MediaPickerDialog({ open, onClose, onSelect }) {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const { media, total, page, loading } = useSelector((state) => state.media);
  const [hoveredId, setHoveredId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (open) dispatch(fetchMedia({ page: 1, limit: ROW_SIZE }));
  }, [open]);

  const handleSelect = (item) => {
    onSelect(item.url, item);
    onClose();
  };

  const handleDelete = async (e, item) => {
    e.stopPropagation();
    const ok = await confirm({
      title: "Delete Media",
      description: `Delete "${item.originalName}"?`,
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setDeletingId(item._id);
    await dispatch(deleteMedia(item._id));
    setDeletingId(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "24px" } }}>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: "Heebo",
          fontWeight: 700,
          color: "#362B24",
          borderBottom: "1px solid #EBEBEB",
          pb: 1.5,
        }}
      >
        Pick from Media Library
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={1.5}>
          {loading
            ? Array.from({ length: ROW_SIZE }).map((_, i) => (
                <Grid item key={i} xs={6} sm={4} md={3}>
                  <Skeleton variant="rounded" width="100%" height={110} sx={{ borderRadius: "12px", mb: 0.5 }} />
                  <Skeleton variant="text" width="70%" height={16} sx={{ borderRadius: "4px" }} />
                </Grid>
              ))
            : media.map((item) => (
                <Grid item key={item._id} xs={6} sm={4} md={3}>
                  <Box
                    onMouseEnter={() => setHoveredId(item._id)}
                    onMouseLeave={() => setHoveredId(null)}
                    sx={{
                      borderRadius: "12px",
                      border: "2px solid",
                      borderColor: hoveredId === item._id ? "#065F46" : "#EBEBEB",
                      overflow: "hidden",
                      bgcolor: "#fff",
                      transition: "border-color 0.15s, box-shadow 0.15s",
                      boxShadow: hoveredId === item._id ? "0 4px 12px rgba(6,95,70,0.12)" : "none",
                      opacity: deletingId === item._id ? 0.5 : 1,
                      pointerEvents: deletingId === item._id ? "none" : "auto",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Image area */}
                    <Box
                      onClick={() => handleSelect(item)}
                      sx={{ position: "relative", cursor: "pointer", bgcolor: "#F9FAFB" }}
                    >
                      <BlogImage
                        src={withCdn(item.url)}
                        alt={item.alt || item.originalName}
                        width="100%"
                        height={110}
                        borderRadius="0"
                        objectFit="contain"
                      />
                      {hoveredId === item._id && (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            bgcolor: "rgba(6,95,70,0.08)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CheckCircleIcon sx={{ color: "#065F46", fontSize: 28 }} />
                        </Box>
                      )}
                    </Box>

                    {/* Footer row */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 1,
                        py: 0.5,
                        borderTop: "1px solid #EBEBEB",
                        minHeight: 32,
                      }}
                    >
                      <Tooltip title={item.originalName} placement="bottom">
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{ fontFamily: "Heebo", color: "#362B24", fontWeight: 500, flex: 1, mr: 0.5 }}
                        >
                          {item.originalName}
                        </Typography>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => handleDelete(e, item)}
                          sx={{ p: 0.25, flexShrink: 0, "&:hover": { bgcolor: "rgba(234,0,27,0.08)" } }}
                        >
                          {deletingId === item._id
                            ? <CircularProgress size={13} sx={{ color: "#EA001B" }} />
                            : <DeleteIcon sx={{ fontSize: 14, color: "#EA001B" }} />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Grid>
              ))
          }

          {!loading && media.length === 0 && (
            <Grid item xs={12}>
              <Typography sx={{ textAlign: "center", color: "#362B24", opacity: 0.5, fontFamily: "Heebo", py: 4 }}>
                No media uploaded yet.
              </Typography>
            </Grid>
          )}
        </Grid>

        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          rowsPerPage={ROW_SIZE}
          rowsPerPageOptions={[ROW_SIZE]}
          onPageChange={(_, pg) => dispatch(fetchMedia({ page: pg + 1, limit: ROW_SIZE }))}
          sx={{
            mt: 1,
            borderTop: "1px solid #EBEBEB",
            "& .MuiTablePagination-toolbar": {
              alignItems: "center",
              minHeight: 40,
              px: 0,
            },
            "& .MuiTablePagination-displayedRows": {
              fontFamily: "Heebo",
              fontSize: "0.8rem",
              color: "#362B24",
              m: 0,
              lineHeight: 1,
            },
            "& .MuiTablePagination-actions": {
              display: "flex",
              alignItems: "center",
              ml: 1,
            },
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
